'use client';

import * as React from 'react';
import { NativeSelect, type NativeSelectProps } from './native-select';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from './select';
import { dropdownIconFor, dropdownIconPaths } from './dropdown-icons';
import { cn } from '@/lib/utils';
import { fieldLayout } from './selection/field-layout';
import { groupOptions, optionsOf } from './selection/options';

/** Radix interaction with a real select for events, refs, validity and FormData. */
export function FieldSelect(props: NativeSelectProps) {
  if (props.multiple || (props.size && props.size > 1) || 'data-field-unstyled' in props || props['aria-hidden'] === true || props['aria-hidden'] === 'true') return <NativeSelect {...props} />;
  return <SingleFieldSelect {...props} />;
}

function SingleFieldSelect({ children, className, style, icon, ref: forwardedRef, onChange, onInvalid, onFocus, onBlur, onKeyDown, ...props }: NativeSelectProps) {
  const native = React.useRef<HTMLSelectElement>(null), trigger = React.useRef<HTMLButtonElement>(null);
  const id = React.useId(), options = optionsOf(children), empty = `${id}-empty`;
  const [uncontrolled, setUncontrolled] = React.useState(() => String(props.defaultValue ?? options.find(option => !option.disabled)?.value ?? ''));
  const [invalid, setInvalid] = React.useState(false), [open, setOpen] = React.useState(false), [hasLeadingIcon, setHasLeadingIcon] = React.useState(false);
  const value = props.value === undefined ? uncontrolled : String(props.value);
  const selected = options.find(option => option.value === value);
  const label = props['aria-label'] || props.name || props.id || '';
  const resolvedIcon = icon === false || hasLeadingIcon || /per page|rows|^size$/i.test(label) ? undefined : icon ?? dropdownIconFor(label);

  const wasOpen = React.useRef(false);
  React.useLayoutEffect(() => {
    // Restore focus before an enclosing dialog's mutation observer sees the
    // removed option. Radix's deferred close autofocus runs after that observer.
    if (wasOpen.current && !open) trigger.current?.focus({ preventScroll: true });
    wasOpen.current = open;
  }, [open]);

  React.useEffect(() => {
    const parent = trigger.current?.parentElement?.parentElement;
    setHasLeadingIcon(!!parent?.querySelector(':scope > svg:not([data-native-select-chevron])') || !!parent?.closest('[data-no-dropdown-icons]'));
  }, []);

  React.useEffect(() => {
    const field = native.current;
    if (field && props.value === undefined && field.value !== uncontrolled) setUncontrolled(field.value);
  }, [props.value, uncontrolled, children]);
  React.useEffect(() => {
    const form = native.current?.form;
    let frame: number | undefined;
    const reset = () => { setOpen(false); setInvalid(false); frame = requestAnimationFrame(() => { if (native.current && props.value === undefined) setUncontrolled(native.current.value); }); };
    form?.addEventListener('reset', reset);
    return () => { form?.removeEventListener('reset', reset); if (frame !== undefined) cancelAnimationFrame(frame); };
  }, [props.form, props.value]);

  function commit(next: string) {
    const field = native.current;
    if (!field || field.matches(':disabled')) return;
    field.value = next === empty ? '' : next;
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));
  }
  function fieldFocus(event: React.FocusEvent<HTMLButtonElement>, handler?: React.FocusEventHandler<HTMLSelectElement>) {
    if (native.current) handler?.({ ...event, target: native.current, currentTarget: native.current } as React.FocusEvent<HTMLSelectElement>);
  }

  const groups = groupOptions(options), layout = fieldLayout(className, style);
  return <span data-slot="field-select" className={cn('field-select', layout.layoutClass)} style={layout.outerStyle}>
    <select {...props} id={props.id ? `${props.id}-native` : undefined} autoFocus={false} aria-label="" aria-labelledby={undefined} aria-hidden="true" tabIndex={-1} data-slot="field-select-native" className="field-select-native" onFocus={() => trigger.current?.focus()} ref={element => {
      native.current = element;
      if (typeof forwardedRef === 'function') return forwardedRef(element);
      if (forwardedRef) forwardedRef.current = element;
    }} onChange={event => { if (props.value === undefined) setUncontrolled(event.target.value); setInvalid(false); onChange?.(event); }}
      onInvalid={event => { onInvalid?.(event); if (!event.defaultPrevented) { event.preventDefault(); setInvalid(true); trigger.current?.focus(); } }}>
      {children}
    </select>
    <Select value={selected ? value || empty : ''} onValueChange={commit} disabled={props.disabled} open={open} onOpenChange={next => { if (!next || !native.current?.matches(':disabled')) setOpen(next); }} dir={props.dir === 'rtl' ? 'rtl' : props.dir === 'ltr' ? 'ltr' : undefined}>
      <SelectTrigger ref={trigger} id={props.id} className={cn('field-select-trigger', layout.appearanceClass)} style={layout.innerStyle} icon={false} title={props.title ?? selected?.label} form={props.form}
        aria-label={props['aria-label']} aria-labelledby={props['aria-labelledby']} aria-describedby={props['aria-describedby']} aria-invalid={props['aria-invalid'] || invalid || undefined} aria-required={props.required || undefined}
        autoFocus={props.autoFocus} tabIndex={props.tabIndex} onFocus={event => fieldFocus(event, onFocus)} onBlur={event => fieldFocus(event, onBlur)}
        onKeyDown={event => { onKeyDown?.(event as unknown as React.KeyboardEvent<HTMLSelectElement>); event.stopPropagation(); }}>
        {resolvedIcon && <svg data-slot="select-context-icon" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: dropdownIconPaths[resolvedIcon] }} />}
        <span className="field-select-labels"><SelectValue placeholder={selected?.label || 'Select an option'}>{selected?.label}</SelectValue><span className="field-select-sizer" aria-hidden="true">{options.map((option, index) => <span key={index} data-label={option.label} />)}</span></span>
      </SelectTrigger>
      <SelectContent aria-label={props['aria-label'] ? `${props['aria-label']} options` : undefined} onKeyDown={event => event.stopPropagation()}>
        {groups.map((group, index) => <SelectGroup key={index}>{group.label && <SelectLabel>{group.label}</SelectLabel>}{group.options.map((option, i) => <SelectItem key={`${option.value}-${i}`} value={option.value || empty} disabled={option.disabled}>{option.label}</SelectItem>)}</SelectGroup>)}
      </SelectContent>
    </Select>
  </span>;
}
